import React, { useState, useEffect, useRef, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    WeeklyPlan, ProjectInfo, GlobalUser, Responsible, Profile, AttendanceRow, WeeklyAttendanceSheet, CompanyConfig
} from '../../types';

interface AttendanceModalProps {
    isOpen: boolean;
    onClose: () => void;
    plan: WeeklyPlan | null;
    projectInfo: ProjectInfo;
    globalUsers?: GlobalUser[];
    responsibles?: Responsible[];
    profiles?: Profile[];
    companyConfig?: CompanyConfig;
    hasEditPermission?: boolean;
}

export const AttendanceModal: React.FC<AttendanceModalProps> = ({
    isOpen,
    onClose,
    plan,
    projectInfo,
    globalUsers = [],
    responsibles = [],
    profiles = [],
    companyConfig,
    hasEditPermission = true
}) => {
    // Generate initial 20 rows
    const createDefaultRows = (): AttendanceRow[] => {
        return Array.from({ length: 20 }, (_, idx) => ({
            id: `att-row-${Date.now()}-${idx + 1}`,
            correlative: idx + 1,
            name: '',
            profileName: '',
            isOther: false
        }));
    };

    const storageKey = `klydapp_attendance_${projectInfo?.name || 'default'}_plan_${plan?.id || 0}`;

    // Rows state
    const [rows, setRows] = useState<AttendanceRow[]>(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const parsed: WeeklyAttendanceSheet = JSON.parse(saved);
                if (parsed.rows && parsed.rows.length > 0) {
                    return parsed.rows;
                }
            }
        } catch (e) {
            console.error('Error loading attendance sheet:', e);
        }
        return createDefaultRows();
    });

    // Reset or reload when plan changes
    useEffect(() => {
        if (!plan) return;
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const parsed: WeeklyAttendanceSheet = JSON.parse(saved);
                if (parsed.rows && parsed.rows.length > 0) {
                    setRows(parsed.rows);
                    return;
                }
            }
        } catch (e) {
            console.error('Error loading attendance sheet:', e);
        }
        setRows(createDefaultRows());
    }, [plan?.id, projectInfo?.name]);

    // UI States
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [saveNotification, setSaveNotification] = useState<string | null>(null);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    // Active dropdown selector for names
    const [openDropdownRowId, setOpenDropdownRowId] = useState<string | null>(null);
    const [userSearchTerm, setUserSearchTerm] = useState('');

    // Active Signature Pad Modal
    const [signatureModalTargetRowId, setSignatureModalTargetRowId] = useState<string | null>(null);

    // Format Date for Title (e.g. DD-MM-YY)
    const formattedDate = useMemo(() => {
        if (!plan?.startDate) return '';
        const parts = plan.startDate.split('-');
        if (parts.length === 3) {
            const [yyyy, mm, dd] = parts;
            return `${dd}-${mm}-${yyyy.slice(-2)}`;
        }
        return plan.startDate;
    }, [plan?.startDate]);

    // Build unified list of users assigned to project
    const availableUsers = useMemo(() => {
        const userMap = new Map<string, { id: string; name: string; role: string; profileId?: string }>();

        // 1. From globalUsers
        globalUsers.filter(u => !u.isDeleted && u.status !== 'Inactive').forEach(u => {
            const assigned = u.assignedProjects?.find(ap => 
                ap.projectId === projectInfo?.id || 
                ap.projectId === projectInfo?.name ||
                (projectInfo?.id && String(ap.projectId).toLowerCase() === String(projectInfo.id).toLowerCase())
            ) || u.assignedProjects?.[0];

            const matchedProfile = assigned 
                ? profiles.find(p => p.id === assigned.profileId || p.name === assigned.profileId) 
                : null;

            // Resolve exact Profile name (e.g. Jefe de Terreno, Oficina Técnica, Supervisor)
            let roleName = assigned?.profileName || matchedProfile?.name;
            if (!roleName || roleName === 'Usuario' || roleName === 'Administrador' || roleName === 'SYSADMIN') {
                const respMatch = responsibles.find(r => r.id === u.id || r.email === u.email || r.name === u.name);
                if (respMatch?.profileId) {
                    const profFromResp = profiles.find(p => p.id === respMatch.profileId || p.name === respMatch.profileId);
                    if (profFromResp?.name) roleName = profFromResp.name;
                } else if (respMatch?.role && respMatch.role !== 'Admin' && respMatch.role !== 'Usuario' && respMatch.role !== 'Administrador') {
                    roleName = respMatch.role;
                }
            }

            if (!roleName || roleName === 'Usuario' || roleName === 'Administrador' || roleName === 'SYSADMIN') {
                roleName = profiles[0]?.name || 'Participante';
            }

            userMap.set(u.id, {
                id: u.id,
                name: u.name,
                role: roleName,
                profileId: assigned?.profileId || matchedProfile?.id
            });
        });

        // 2. From responsibles (merge / add missing)
        responsibles.forEach(r => {
            const matchedProfile = r.profileId 
                ? profiles.find(p => p.id === r.profileId || p.name === r.profileId) 
                : null;

            let roleName = matchedProfile?.name;
            if (!roleName && r.role && r.role !== 'Admin' && r.role !== 'Usuario' && r.role !== 'Administrador') {
                roleName = r.role;
            }

            if (!userMap.has(r.id)) {
                userMap.set(r.id, {
                    id: r.id,
                    name: r.name,
                    role: roleName || profiles[0]?.name || 'Participante',
                    profileId: r.profileId || matchedProfile?.id
                });
            } else if (roleName) {
                const existing = userMap.get(r.id)!;
                if (!existing.role || existing.role === 'Usuario' || existing.role === 'Administrador' || existing.role === 'Participante') {
                    userMap.set(r.id, { ...existing, role: roleName });
                }
            }
        });

        return Array.from(userMap.values()).sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
    }, [globalUsers, responsibles, profiles, projectInfo]);

    // Active profiles list for cargo dropdown
    const availableProfiles = useMemo(() => {
        return profiles.filter(p => !p.isDeleted).sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
    }, [profiles]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.user-dropdown-container')) {
                setOpenDropdownRowId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!isOpen || !plan) return null;

    // Row handlers
    const handleUpdateRow = (rowId: string, patch: Partial<AttendanceRow>) => {
        setRows(prev => prev.map(r => r.id === rowId ? { ...r, ...patch } : r));
        setHasUnsavedChanges(true);
    };

    const handleSelectUser = (rowId: string, user: { id: string; name: string; role: string; profileId?: string }) => {
        handleUpdateRow(rowId, {
            userId: user.id,
            name: user.name,
            profileName: user.role,
            profileId: user.profileId,
            isOther: false
        });
        setOpenDropdownRowId(null);
        setUserSearchTerm('');
    };

    const handleSelectOther = (rowId: string) => {
        handleUpdateRow(rowId, {
            userId: 'other',
            name: '',
            profileName: availableProfiles[0]?.name || '',
            profileId: availableProfiles[0]?.id || '',
            isOther: true
        });
        setOpenDropdownRowId(null);
        setUserSearchTerm('');
    };

    const handleAddRow = () => {
        const newRow: AttendanceRow = {
            id: `att-row-${Date.now()}-${rows.length + 1}`,
            correlative: rows.length + 1,
            name: '',
            profileName: '',
            isOther: false
        };
        setRows(prev => [...prev, newRow]);
        setHasUnsavedChanges(true);
    };

    const handleDeleteRow = (rowId: string) => {
        setRows(prev => {
            const filtered = prev.filter(r => r.id !== rowId);
            return filtered.map((r, idx) => ({ ...r, correlative: idx + 1 }));
        });
        setHasUnsavedChanges(true);
    };

    const handleSaveSheet = () => {
        try {
            const sheetData: WeeklyAttendanceSheet = {
                planId: plan.id,
                projectName: projectInfo?.name || 'Obra',
                startDate: plan.startDate,
                rows,
                updatedAt: new Date().toISOString()
            };
            localStorage.setItem(storageKey, JSON.stringify(sheetData));
            setHasUnsavedChanges(false);
            setSaveNotification('Registro de asistencia guardado exitosamente.');
            setTimeout(() => setSaveNotification(null), 3000);
        } catch (e) {
            console.error('Error saving attendance sheet:', e);
            alert('Error al guardar el registro de asistencia.');
        }
    };

    // Load image as Base64 / Image element for PDF
    const loadImageBase64 = async (url: string): Promise<string | null> => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
            });
        } catch {
            return null;
        }
    };

    // Export to PDF matching the user's requested layout exactly
    const handleExportPdf = async () => {
        setIsGeneratingPdf(true);
        try {
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 12;

            // 1. Logo
            const logoSrc = companyConfig?.logo || '/assets/logo_ld.png';
            const logoData = await loadImageBase64(logoSrc);
            if (logoData) {
                try {
                    doc.addImage(logoData, 'PNG', margin, 10, 32, 14, undefined, 'FAST');
                } catch (e) {
                    console.error('Error inserting logo in PDF', e);
                }
            }

            // 2. Encabezado Central
            const obraText = (projectInfo?.name || 'NOMBRE DE OBRA').toUpperCase();
            const semanaText = `PLANIFICACIÓN SEMANAL #${plan.id}`;
            const fechaText = `FECHA: ${formattedDate || plan.startDate}`;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.setTextColor(0, 0, 0);
            doc.text(obraText, pageWidth / 2 + 10, 14, { align: 'center' });

            doc.setFontSize(14);
            doc.text(semanaText, pageWidth / 2 + 10, 20.5, { align: 'center' });

            doc.setFontSize(11);
            doc.text(fechaText, pageWidth / 2 + 10, 26, { align: 'center' });

            // 3. Tabla Formal (Idéntica a la plantilla de referencia)
            const tableData = rows.map(r => [
                r.correlative.toString(),
                r.name || '',
                r.profileName || '',
                '' // Espacio para firma
            ]);

            autoTable(doc, {
                startY: 30,
                margin: { left: margin, right: margin },
                head: [['#', 'NOMBRE', 'CARGO', 'FIRMA']],
                body: tableData,
                theme: 'plain',
                styles: {
                    font: 'helvetica',
                    fontSize: 8.5,
                    textColor: [0, 0, 0],
                    lineColor: [0, 0, 0],
                    lineWidth: 0.25,
                    cellPadding: { top: 2.2, bottom: 2.2, left: 3, right: 3 },
                    valign: 'middle'
                },
                headStyles: {
                    fontStyle: 'bold',
                    fontSize: 10,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    lineWidth: 0.35,
                    lineColor: [0, 0, 0],
                    halign: 'left'
                },
                columnStyles: {
                    0: { cellWidth: 12, halign: 'center', fontStyle: 'bold' },
                    1: { cellWidth: 78, halign: 'left' },
                    2: { cellWidth: 52, halign: 'left' },
                    3: { cellWidth: 44, halign: 'center' }
                },
                didParseCell: (data) => {
                    if (data.section === 'head' && data.column.index === 0) {
                        data.cell.styles.halign = 'center';
                    }
                    if (data.section === 'head' && data.column.index === 3) {
                        data.cell.styles.halign = 'center';
                    }
                },
                didDrawCell: (data) => {
                    if (data.section === 'body' && data.column.index === 3) {
                        const rowIndex = data.row.index;
                        const rowItem = rows[rowIndex];
                        if (rowItem && rowItem.signature) {
                            try {
                                const cell = data.cell;
                                const imgW = 28;
                                const imgH = Math.min(cell.height - 1.5, 7.5);
                                const imgX = cell.x + (cell.width - imgW) / 2;
                                const imgY = cell.y + (cell.height - imgH) / 2;
                                doc.addImage(rowItem.signature, 'PNG', imgX, imgY, imgW, imgH, undefined, 'FAST');
                            } catch (err) {
                                console.error('Error drawing signature in PDF cell:', err);
                            }
                        }
                    }
                }
            });

            // Nombre del archivo PDF
            const cleanProjectName = (projectInfo?.name || 'Obra').replace(/[^a-zA-Z0-9_-]/g, '_');
            doc.save(`Asistencia_Planificacion_Semanal_${plan.id}_${cleanProjectName}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error al generar el archivo PDF de asistencia.');
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200 print:p-0 print:bg-white print:static">
            
            {/* Ventana Única Integrada (Sin marcos dobles ni ventanas anidadas) */}
            <div className="bg-white text-slate-900 rounded-2xl shadow-2xl border border-slate-300 w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden print:h-auto print:shadow-none print:border-none print:rounded-none">
                
                {/* 1. ENCABEZADO SUPERIOR FIJO (Logo + Títulos Centrales + Acciones) */}
                <div className="px-6 py-4 border-b-2 border-black bg-white shrink-0">
                    <div className="relative flex items-center justify-between">
                        
                        {/* Logo LD Izquierda */}
                        <div className="w-32 md:w-36 flex items-center shrink-0">
                            <img
                                src={companyConfig?.logo || '/assets/logo_ld.png'}
                                alt="LD CONSTRUCTORA"
                                className="max-h-12 max-w-full object-contain"
                            />
                        </div>

                        {/* Títulos Centrales Formales */}
                        <div className="flex-1 text-center px-4">
                            <h1 className="text-lg md:text-xl font-black tracking-tight uppercase text-black leading-tight">
                                {projectInfo?.name || 'NOMBRE DE OBRA'}
                            </h1>
                            <h2 className="text-base md:text-lg font-black tracking-tight uppercase text-black mt-0.5">
                                PLANIFICACIÓN SEMANAL #{plan.id}
                            </h2>
                            <p className="text-xs md:text-sm font-black uppercase text-black mt-0.5">
                                FECHA: {formattedDate || plan.startDate}
                            </p>
                        </div>

                        {/* Botones de Acción Superiores Derecha */}
                        <div className="flex items-center gap-2 shrink-0 print:hidden">
                            {saveNotification && (
                                <span className="text-xs font-bold text-emerald-600 flex items-center gap-1 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200 animate-in fade-in duration-200">
                                    <span className="material-symbols-outlined text-[15px]">check_circle</span>
                                    {saveNotification}
                                </span>
                            )}

                            {/* Botón Descargar PDF */}
                            <button
                                onClick={handleExportPdf}
                                disabled={isGeneratingPdf}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 active:scale-95 text-white text-xs font-bold uppercase tracking-wider rounded-xl shadow-sm transition-all cursor-pointer disabled:opacity-50"
                                title="Descargar documento PDF formal"
                            >
                                <span className="material-symbols-outlined text-[17px]">
                                    {isGeneratingPdf ? 'hourglass_top' : 'picture_as_pdf'}
                                </span>
                                <span className="hidden sm:inline">{isGeneratingPdf ? 'Generando...' : 'Descargar PDF'}</span>
                            </button>

                            {/* Botón Guardar */}
                            {hasEditPermission && (
                                <button
                                    onClick={handleSaveSheet}
                                    className={`flex items-center gap-1.5 px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-sm transition-all cursor-pointer ${
                                        hasUnsavedChanges ? 'ring-2 ring-emerald-400 ring-offset-1' : ''
                                    }`}
                                >
                                    <span className="material-symbols-outlined text-[17px]">save</span>
                                    <span className="hidden sm:inline">Guardar</span>
                                </button>
                            )}

                            {/* Cerrar */}
                            <button
                                onClick={onClose}
                                className="p-1.5 rounded-xl text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors cursor-pointer"
                                title="Cerrar ventana"
                            >
                                <span className="material-symbols-outlined text-[22px]">close</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* 2. TABLA CON ENCABEZADO STICKY Y FILAS DESPLAZABLES */}
                <div className="flex-1 overflow-y-auto bg-white px-6 py-4">
                    <div className="border-2 border-black">
                        <table className="w-full text-left border-collapse text-xs">
                            {/* ENCABEZADO DE TABLA STICKY (Permanente al hacer scroll) */}
                            <thead className="sticky top-0 z-20 bg-white shadow-xs">
                                <tr className="border-b-2 border-black bg-white text-black font-black uppercase text-xs tracking-wider">
                                    <th className="border-r-2 border-black px-2 py-2.5 w-12 text-center bg-white">#</th>
                                    <th className="border-r-2 border-black px-4 py-2.5 bg-white">NOMBRE</th>
                                    <th className="border-r-2 border-black px-4 py-2.5 min-w-[200px] bg-white">CARGO</th>
                                    <th className="px-4 py-2.5 min-w-[170px] text-center bg-white">FIRMA</th>
                                </tr>
                            </thead>
                            
                            {/* FILAS DE LA TABLA */}
                            <tbody className="divide-y divide-black font-medium text-black">
                                {rows.map((row) => (
                                    <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                                        
                                        {/* 1. # (Correlativo) */}
                                        <td className="border-r-2 border-black px-2 py-2 text-center font-bold text-black bg-white">
                                            {row.correlative}
                                        </td>

                                        {/* 2. NOMBRE */}
                                        <td className="border-r-2 border-black px-3 py-1.5 bg-white">
                                            {row.isOther ? (
                                                <div className="flex items-center gap-1.5">
                                                    <input
                                                        type="text"
                                                        value={row.name}
                                                        onChange={e => handleUpdateRow(row.id, { name: e.target.value })}
                                                        disabled={!hasEditPermission}
                                                        placeholder="Escribir nombre..."
                                                        className="w-full border border-blue-400 bg-blue-50/50 rounded px-2 py-1 text-xs font-bold text-black outline-none focus:ring-1 focus:ring-blue-500"
                                                        autoFocus
                                                    />
                                                    {hasEditPermission && (
                                                        <button
                                                            onClick={() => handleUpdateRow(row.id, { isOther: false, name: '', profileName: '' })}
                                                            className="p-1 text-slate-400 hover:text-blue-600 rounded transition-colors shrink-0 cursor-pointer print:hidden"
                                                            title="Volver a la lista de participantes"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px]">swap_horiz</span>
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="relative user-dropdown-container">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (!hasEditPermission) return;
                                                            setOpenDropdownRowId(openDropdownRowId === row.id ? null : row.id);
                                                            setUserSearchTerm('');
                                                        }}
                                                        disabled={!hasEditPermission}
                                                        className={`w-full text-left px-2 py-1 rounded flex items-center justify-between transition-all ${
                                                            row.name 
                                                                ? 'text-black font-bold' 
                                                                : 'text-slate-400 hover:text-slate-600 italic'
                                                        } ${hasEditPermission ? 'cursor-pointer hover:bg-slate-100' : 'cursor-default'}`}
                                                    >
                                                        <span className="truncate">{row.name || '—'}</span>
                                                        {hasEditPermission && (
                                                            <span className="material-symbols-outlined text-[14px] text-slate-400 shrink-0 print:hidden">
                                                                expand_more
                                                            </span>
                                                        )}
                                                    </button>

                                                    {/* Dropdown Popover */}
                                                    {openDropdownRowId === row.id && (
                                                        <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-xl shadow-2xl border border-slate-300 z-50 overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-100">
                                                            {/* Search Bar */}
                                                            <div className="p-2 border-b border-slate-200 bg-slate-50">
                                                                <div className="relative">
                                                                    <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[16px]">search</span>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Buscar participante..."
                                                                        value={userSearchTerm}
                                                                        onChange={e => setUserSearchTerm(e.target.value)}
                                                                        className="w-full pl-8 pr-2.5 py-1 bg-white border border-slate-300 rounded-lg text-xs font-medium text-black outline-none focus:ring-1 focus:ring-blue-500"
                                                                        autoFocus
                                                                        onClick={e => e.stopPropagation()}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* User List */}
                                                            <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                                                                {availableUsers
                                                                    .filter(u => u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) || u.role.toLowerCase().includes(userSearchTerm.toLowerCase()))
                                                                    .map(user => (
                                                                        <button
                                                                            key={user.id}
                                                                            type="button"
                                                                            onClick={() => handleSelectUser(row.id, user)}
                                                                            className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors flex items-center justify-between cursor-pointer"
                                                                        >
                                                                            <span className="font-bold text-xs text-black">{user.name}</span>
                                                                            <span className="text-[10px] font-semibold text-slate-500 ml-2 truncate max-w-[110px]">
                                                                                {user.role}
                                                                            </span>
                                                                        </button>
                                                                    ))}

                                                                {availableUsers.filter(u => u.name.toLowerCase().includes(userSearchTerm.toLowerCase())).length === 0 && (
                                                                    <div className="p-3 text-center text-xs text-slate-400 italic">
                                                                        No se encontraron participantes
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Option 'Otro' at bottom */}
                                                            <div className="p-1.5 border-t border-slate-200 bg-slate-50">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSelectOther(row.id)}
                                                                    className="w-full text-left px-3 py-1.5 rounded-lg text-xs font-bold text-blue-600 hover:bg-blue-100 flex items-center gap-1.5 transition-colors cursor-pointer"
                                                                >
                                                                    <span className="material-symbols-outlined text-[16px]">edit</span>
                                                                    Otro (Ingresar manualmente)
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </td>

                                        {/* 3. CARGO */}
                                        <td className="border-r-2 border-black px-3 py-1.5 bg-white">
                                            {row.isOther ? (
                                                <select
                                                    value={row.profileName}
                                                    onChange={e => {
                                                        const selectedName = e.target.value;
                                                        const matched = availableProfiles.find(p => p.name === selectedName);
                                                        handleUpdateRow(row.id, {
                                                            profileName: selectedName,
                                                            profileId: matched?.id
                                                        });
                                                    }}
                                                    disabled={!hasEditPermission}
                                                    className="w-full border border-slate-300 rounded px-2 py-1 bg-white text-black font-semibold text-xs outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                                                >
                                                    <option value="">Seleccionar cargo...</option>
                                                    {availableProfiles.map(p => (
                                                        <option key={p.id} value={p.name}>{p.name}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="text-black font-medium block px-1 py-0.5">
                                                    {row.profileName || '—'}
                                                </span>
                                            )}
                                        </td>

                                        {/* 4. FIRMA */}
                                        <td className="px-3 py-1 text-center bg-white">
                                            {row.signature ? (
                                                <div className="flex items-center justify-center gap-1.5">
                                                    <div className="h-8 w-28 bg-white border border-slate-200 rounded p-0.5 flex items-center justify-center overflow-hidden">
                                                        <img src={row.signature} alt="Firma" className="max-h-full max-w-full object-contain" />
                                                    </div>
                                                    {hasEditPermission && (
                                                        <div className="flex flex-col gap-0.5 print:hidden">
                                                            <button
                                                                onClick={() => setSignatureModalTargetRowId(row.id)}
                                                                className="p-0.5 text-slate-400 hover:text-blue-600 rounded cursor-pointer"
                                                                title="Re-firmar"
                                                            >
                                                                <span className="material-symbols-outlined text-[14px]">edit</span>
                                                            </button>
                                                            <button
                                                                onClick={() => handleUpdateRow(row.id, { signature: undefined, signedAt: undefined })}
                                                                className="p-0.5 text-slate-400 hover:text-red-600 rounded cursor-pointer"
                                                                title="Borrar firma"
                                                            >
                                                                <span className="material-symbols-outlined text-[14px]">delete</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => hasEditPermission && setSignatureModalTargetRowId(row.id)}
                                                    disabled={!hasEditPermission}
                                                    className={`px-2.5 py-1 rounded text-[11px] font-bold transition-all flex items-center justify-center gap-1 mx-auto ${
                                                        hasEditPermission 
                                                            ? 'text-blue-600 hover:bg-blue-50 border border-blue-200 cursor-pointer active:scale-95' 
                                                            : 'text-slate-400 cursor-not-allowed'
                                                    } print:hidden`}
                                                >
                                                    <span className="material-symbols-outlined text-[14px]">draw</span>
                                                    <span>Firmar</span>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 3. PIE DE VENTANA FIJO */}
                <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between shrink-0 print:hidden">
                    <div>
                        {hasEditPermission && (
                            <button
                                onClick={handleAddRow}
                                className="flex items-center gap-1.5 px-3.5 py-1.5 bg-white hover:bg-slate-100 border border-slate-300 text-slate-800 font-bold text-xs rounded-lg shadow-xs transition-all active:scale-95 cursor-pointer"
                            >
                                <span className="material-symbols-outlined text-[16px] text-blue-600">add_circle</span>
                                + Añadir Fila
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-4">
                        <span className="text-xs text-slate-500 font-medium">
                            Total Filas: <strong className="text-black">{rows.length}</strong>
                        </span>
                        <button
                            onClick={onClose}
                            className="px-4 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>

            {/* Signature Drawing Modal */}
            {signatureModalTargetRowId && (
                <DigitalSignaturePadModal
                    isOpen={Boolean(signatureModalTargetRowId)}
                    onClose={() => setSignatureModalTargetRowId(null)}
                    onSaveSignature={(signatureDataUrl) => {
                        handleUpdateRow(signatureModalTargetRowId, {
                            signature: signatureDataUrl,
                            signedAt: new Date().toISOString()
                        });
                        setSignatureModalTargetRowId(null);
                    }}
                    attendeeName={rows.find(r => r.id === signatureModalTargetRowId)?.name}
                />
            )}
        </div>
    );
};

// --- Dedicated Digital Signature Pad Modal ---
const DigitalSignaturePadModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSaveSignature: (signatureData: string) => void;
    attendeeName?: string;
}> = ({ isOpen, onClose, onSaveSignature, attendeeName }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasDrawn, setHasDrawn] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setTimeout(() => {
            const canvas = canvasRef.current;
            if (canvas) {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.lineWidth = 3;
                    ctx.lineCap = 'round';
                    ctx.lineJoin = 'round';
                    ctx.strokeStyle = '#0f172a';
                }
            }
        }, 50);
    }, [isOpen]);

    const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        if ('touches' in e) {
            return {
                x: e.touches[0].clientX - rect.left,
                y: e.touches[0].clientY - rect.top
            };
        }
        return {
            x: (e as React.MouseEvent).clientX - rect.left,
            y: (e as React.MouseEvent).clientY - rect.top
        };
    };

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        setIsDrawing(true);
        setHasDrawn(true);
        const coords = getCoordinates(e);
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx && coords) {
            ctx.beginPath();
            ctx.moveTo(coords.x, coords.y);
            if (e.cancelable) e.preventDefault();
        }
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const coords = getCoordinates(e);
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx && coords) {
            ctx.lineTo(coords.x, coords.y);
            ctx.stroke();
            if (e.cancelable) e.preventDefault();
        }
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const handleClear = () => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                setHasDrawn(false);
            }
        }
    };

    const handleSave = () => {
        const canvas = canvasRef.current;
        if (!canvas || !hasDrawn) {
            alert('Por favor trace su firma en el recuadro antes de guardar.');
            return;
        }
        const dataUrl = canvas.toDataURL('image/png');
        onSaveSignature(dataUrl);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-150">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-lg p-5 flex flex-col gap-4">
                <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-blue-600 text-[22px]">draw</span>
                        <div>
                            <h4 className="text-sm font-extrabold text-slate-800 dark:text-white uppercase">
                                Trazar Firma Digital
                            </h4>
                            {attendeeName && (
                                <p className="text-xs text-slate-500 font-medium">Participante: {attendeeName}</p>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>

                {/* Signature Canvas Box */}
                <div className="relative bg-slate-50 dark:bg-slate-950 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl overflow-hidden touch-none flex flex-col items-center justify-center">
                    <canvas
                        ref={canvasRef}
                        width={460}
                        height={180}
                        className="bg-transparent cursor-crosshair w-full h-[180px]"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                    />
                    {!hasDrawn && (
                        <div className="absolute pointer-events-none text-slate-300 dark:text-slate-600 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-[18px]">gesture</span>
                            Firme aquí con el cursor o pantalla táctil
                        </div>
                    )}
                </div>

                {/* Controls */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-800">
                    <button
                        onClick={handleClear}
                        className="flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-red-600 transition-colors cursor-pointer"
                    >
                        <span className="material-symbols-outlined text-[16px]">restart_alt</span>
                        Limpiar Trazo
                    </button>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white text-xs font-bold rounded-xl shadow transition-all flex items-center gap-1 cursor-pointer"
                        >
                            <span className="material-symbols-outlined text-[16px]">check</span>
                            Aceptar Firma
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
